import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ControlType, IResourceListItem } from './Models';
import { TextField, CircularProgress, makeStyles, Theme, createStyles, Typography } from '@material-ui/core';
import throttle from 'lodash/throttle';
import Autocomplete from '@material-ui/lab/Autocomplete';
import axios from 'axios';
import SearchDialog from './SearchDialog';
import SelectComputer from './SelectComputer';
import { resourceDescriptionHtml } from '../../utils/ResourceFormat';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		actions: {
			marginBottom: '7px',
		},
		actions__btn: {
			marginRight: '7px',
		},

		option__name: {
			'&:before': {
				display: 'inline-block',
				paddingRight: '5px',
				paddingLeft: '5px',
				color: '#6c757d',
				content: '"-"',
			},
		},
		option__description: {
			'&:before': {
				display: 'inline-block',
				paddingRight: '5px',
				paddingLeft: '5px',
				color: '#6c757d',
				content: '"/"',
			},
		}
	}),
);

interface IRequestData {
	input: string,
	getEmployeesValue: () => string,
}

interface IResourcesAutocompleteProps {
	value: IResourceListItem[],
	onSelect: (value: IResourceListItem[]) => void,
	filterId: string,
	filters: string;
	kind: string,
	multiple?: true | false,
	placeholderText: string,

	controlType: ControlType,
	getNotifierId: () => string,
	getEmployeesValue: () => string,
	disabled: boolean,
	errorText: string,
	isLogSystemIntegrationAvailable: boolean,
}

function ResourcesAutocomplete(props: IResourcesAutocompleteProps) {
	const { t } = useTranslation();
	const classes = useStyles();

	const [loading, setLoading] = React.useState(false);
	const [options, setOptions] = React.useState<IResourceListItem[]>([]);
	const [inputValue, setInputValue] = React.useState('');
	
	const fetch = React.useMemo(
		() =>
			throttle((request: IRequestData, callback: (results?: IResourceListItem[]) => void) => {

				if (props.kind === 'ServiceAsset') {
					axios.get<IResourceListItem[]>("/Resources/GetServiceResourcesAutocomplete/", {
						params: {
							maxRows: 10,
							filterId: props.filterId,
							filters: props.filters,
							startsWith: request.input,
							kind: props.kind,
						}
					})
						.then(x => callback(x.data))
						.catch(x => console.error(x));
				}

				if (props.controlType === 'RelatedResource') {

					if (!props.getNotifierId) {
						console.warn('Unknown \'getNotifierId\'.');
						callback([]);
						return;
					}

					axios.get<IResourceListItem[]>("/Resources/GetRelatedResourcesAutocomplete/", {
						params: {
							maxRows: 10,
							filterId: props.filterId,
							filters: props.filters,
							startsWith: request.input,
							userId: props.getNotifierId(),
							kind: props.kind,
						}
					})
						.then(x => callback(x.data))
						.catch(x => console.error(x));

					return;
				}

				if (props.controlType === 'EmployeeRelatedResources') {
					axios.get<IResourceListItem[]>("/Resources/GetEmployeeRelatedResourcesAutocomplete/", {
						params: {
							maxRows: 10,
							filterId: props.filterId,
							filters: props.filters,
							startsWith: request.input,
							kind: props.kind,

							employees: request.getEmployeesValue(),
						}
					})
						.then(x => callback(x.data))
						.catch(x => console.error(x));

					return;
				}

				axios.get<IResourceListItem[]>("/Resources/GetResourcesAutocomplete/", {
					params: {
						maxRows: 10,
						filterId: props.filterId,
						filters: props.filters,
						startsWith: request.input,
						kind: props.kind,
					}
				})
					.then(x => callback(x.data))
					.catch(x => console.error(x));

			}, 200),
		[],
	);

	React.useEffect(() => {
		
		if (inputValue === '') {
			setOptions([]);
			return;
		}

		if (!props.isLogSystemIntegrationAvailable)
			return;

		let active = true;
		setLoading(true);

		let req: IRequestData = {
			input: inputValue,
			getEmployeesValue: props.getEmployeesValue,
		};
		fetch(req, (results?: IResourceListItem[]) => {
			if (active) {
				var items = results || [];
				setOptions(items);
			}
			setLoading(false);
		});

		return () => {
			active = false;
		};
	}, [inputValue, fetch]);

	function singleValue(v: IResourceListItem[]): IResourceListItem {
		if (!v || v.length == 0)
			return null;
		return v[0];
	}

	let noOptionsText = t("control.autocomplete.enterTextToSearch") + "...";
	if (inputValue) {
		noOptionsText = t("control.autocomplete.noOptionsFoundForPhrase", { text: inputValue});
	}
	if (!props.isLogSystemIntegrationAvailable) {
		noOptionsText = t("common.message.logSystemIntegrationDisabled");
	}

	let placeholder = t("common.action.search") + "...";
	if (props.placeholderText) {
		placeholder = props.placeholderText;
	};
	const renderInput = params => (
		<TextField
			{...params}
			fullWidth
			variant="outlined"
			onChange={(event) => setInputValue(event.target.value)}
			helperText={props.errorText}
			error={!!props.errorText}
			InputProps={{
				...params.InputProps,
				autoComplete: 'disabled',
				placeholder: placeholder,
				endAdornment: (
					<React.Fragment>
						{loading ? <CircularProgress color="inherit" size={20} /> : null}
						{params.InputProps.endAdornment}
					</React.Fragment>
				),
			}}
		/>
	);

	const renderOption = (option: IResourceListItem) => {
		
		var format = resourceDescriptionHtml(option);
		return (
			<div>
				{format.name.map((x, i) => <span className={clsx({[classes.option__name] : i > 0})} key={x.label} title={x.title}>{x.label}</span>)}
				<Typography variant="body2" color="textSecondary">
					{format.description.map((x, i) => <span className={clsx(i > 0 ? classes.option__description : '')} key={x.label} title={x.title}>{x.label}</span>)}
				</Typography>
			</div>
		);
	};

	if (!props.multiple) {
		return (<Autocomplete
			disabled={props.disabled}
			value={singleValue(props.value)}
			onChange={(event, newValue) => {
				if (!newValue)
					props.onSelect([]);
				else {
					let v = newValue as IResourceListItem;
					props.onSelect([v]);
				}
				setInputValue('');
			}}
			getOptionLabel={(option: IResourceListItem) => option.Name}
			getOptionSelected={(option: IResourceListItem, value: IResourceListItem) => option.Id == value.Id}
			options={options}
			loading={loading}
			filterOptions={x => x}

			autoComplete
			includeInputInList
			noOptionsText={noOptionsText}
			renderInput={renderInput}

			renderOption={renderOption}
		/>);
	}
	else {
		return (<Autocomplete
			disabled={props.disabled}
			multiple
			value={props.value}
			onChange={(event, newValue) => {
				let v = newValue as IResourceListItem[];
				props.onSelect(v);
				setInputValue('');
			}}
			getOptionLabel={(option: IResourceListItem) => option.Name}
			getOptionSelected={(option: IResourceListItem, value: IResourceListItem) => option.Id == value.Id}
			options={options}
			loading={loading}
			filterOptions={x => x}
			autoComplete
			includeInputInList
			noOptionsText={noOptionsText}

			renderInput={renderInput}
			renderOption={renderOption}
		/>);
	}
}

interface IProps {
	multiple: boolean,
	kind: string,
	filterId: string,
	filters: string;
	resources: IResourceListItem[],
	onChange: (resources: IResourceListItem[]) => void,
	controlType: ControlType,
	controlNotifierId: string,
	showGridTabs: string,

	resourcesGridColumns: any,
	relatedResourcesGridColumns: any,
	relatedEmployeesResourcesGridColumns: any,
	servicesGridColumns: any,

	requestId: string,
	isModal: boolean,
	disabled: boolean,
	manageFiltersPermission: boolean,
	errorText?: string,
	isLogSystemIntegrationAvailable: boolean,

	getEmployeesValue?: () => string | false;
}

export default function ResourcesEditor(props: IProps) {
	
	function getEmployeesValue(): string {

		if (props.getEmployeesValue) {
			var employeesStr = props.getEmployeesValue();
			if (employeesStr)
				return employeesStr;
		}

		var employeeInput = (window as any).$("input[data-kind='Employee'][data-request='" + props.requestId + "']")[0];
		if (!employeeInput)
			return null;
		return employeeInput.value;
	}

	function getNotifierId() {
		let controlId = props.controlNotifierId;
		if (!controlId)
			controlId = "Notifier_Id";

		let notifier = document.getElementById(controlId) as HTMLInputElement;
		if (!notifier) {
			console.warn(`Nofifier Input not found. ${controlId}`);
			return "";
		}
		return notifier.value;
	}

	return <ResourcesEditorBase
		{...props}
		getEmployeesValue={getEmployeesValue}
		getNotifierId={getNotifierId}
	/>
}

interface IResourcesEditorBaseProps {
	multiple: boolean,
	kind: string,
	filterId: string,
	filters: string;
	resources: IResourceListItem[],
	onChange: (resources: IResourceListItem[]) => void,

	controlType: ControlType,
	showGridTabs: string,

	resourcesGridColumns: any,
	relatedResourcesGridColumns: any,
	relatedEmployeesResourcesGridColumns: any,
	servicesGridColumns: any,

	isModal: boolean,
	disabled?: boolean,
	manageFiltersPermission: boolean,
	errorText?: string,
	isLogSystemIntegrationAvailable: boolean,

	getEmployeesValue: () => string,
	getNotifierId: () => string,
}

export function ResourcesEditorBase(props: IResourcesEditorBaseProps) {
	const classes = useStyles();
	
	function isSelected(newResource: IResourceListItem): boolean {
		for (let i = 0; i < props.resources.length; i++) {
			let resource = props.resources[i];
			if (resource.Id == newResource.Id) {
				return true;
			}
		}
		return false;
	}

	return <>
		{props.isLogSystemIntegrationAvailable && <div className={classes.actions}>
			<SearchDialog
				disabled={props.disabled}
				className={classes.actions__btn}
				getEmployeesValue={props.getEmployeesValue}
				getNotifierId={props.getNotifierId}
				kind={props.kind}
				filterId={props.filterId}
				filters={props.filters}
				showGridTabs={props.showGridTabs}
				relatedEmployeesResourcesGridColumns={props.relatedEmployeesResourcesGridColumns}
				relatedResourcesGridColumns={props.relatedResourcesGridColumns}
				resourcesGridColumns={props.resourcesGridColumns}
				servicesGridColumns={props.servicesGridColumns}
				manageFiltersPermission={props.manageFiltersPermission}

				onSave={(newResources) => {

					if (!newResources)
						return;

					if (!props.multiple) {
						let newResource = newResources[0];
						if (newResource) {
							props.onChange([newResource]);
							return;
						}
						props.onChange([]);
						return;
					}

					let resources = [...props.resources];
					for (let i = 0; i < newResources.length; i++) {
						let newResource = newResources[i];
						if (!isSelected(newResource))
							resources.push(newResource);
					}

					props.onChange(resources);
				}}
			/>
			<SelectComputer
				disabled={props.disabled}
				className={classes.actions__btn}
				isModal={props.isModal}
				kind={props.kind}
				onSave={(newResource) => {
					if (!newResource)
						return;

					let resources = [...props.resources];
					if (!isSelected(newResource))
						resources.push(newResource);

					props.onChange(resources);
				}}
			/>
		</div>}
		<ResourcesAutocomplete
			disabled={props.disabled}
			kind={props.kind}
			value={props.resources}
			multiple={props.multiple}
			onSelect={(r) => {
				props.onChange(r);
			}}
			filterId={props.filterId}
			filters={props.filters}
			placeholderText=""
			controlType={props.controlType}

			getEmployeesValue={props.getEmployeesValue}
			getNotifierId={props.getNotifierId}
			errorText={props.errorText}
			isLogSystemIntegrationAvailable={props.isLogSystemIntegrationAvailable}
		/>
	</>;
}